import type {
  BadmintonGamePlayerItem,
  BadmintonSessionListItem,
} from '@/services/badmintonService';

export function formatGameStatusLabel(status: string): string {
  switch (status) {
    case 'completed':
      return 'Selesai';
    case 'in_progress':
      return 'Berlangsung';
    case 'scheduled':
      return 'Belum Main';
    default:
      return 'Belum Main';
  }
}

export function formatBadmintonGameStatus(status: string): string {
  return formatGameStatusLabel(status);
}

export function formatSetPair(setsWon: number, setsLost: number): string {
  return `${setsWon}-${setsLost}`;
}

export function formatPointPair(pointsScored: number, pointsConceded: number): string {
  return `${pointsScored}-${pointsConceded}`;
}

export function formatWinRatePercent(winRate: number): string {
  return `${Math.round(winRate)}%`;
}

export function formatDeltaMmr(deltaMmr: number): string {
  return deltaMmr >= 0 ? `+${deltaMmr}` : `${deltaMmr}`;
}

export function formatTopMmrSubtitle(
  wins: number,
  losses: number,
  setsWon: number,
  setsLost: number
): string {
  return `${wins}W · ${losses}L · set ${formatSetPair(setsWon, setsLost)}`;
}

export function formatBadmintonSessionStatus(status: string): string {
  switch (status) {
    case 'completed':
      return 'Selesai';
    case 'in_progress':
      return 'Berlangsung';
    default:
      return 'Draft';
  }
}

export function formatPlayerFormat(fmt: string): string {
  return fmt === 'doubles' ? 'Ganda' : 'Tunggal';
}

export function formatBadmintonSessionTypeLabel(
  sessionType: string,
  playerFormat?: string
): string {
  if (sessionType === 'manual') return 'Manual';
  if (sessionType === 'generated') {
    return playerFormat === 'singles' ? 'Tunggal' : 'Ganda';
  }
  return sessionType;
}

export function badmintonGameStatusBackgroundAndForeground(status: string): string {
  switch (status) {
    case 'completed':
      return 'bg-[#EFF6FF] text-[#22C55E] text-[10px] font-extrabold';
    case 'in_progress':
      return 'bg-[#FEF0EA] text-[#F59E0B] text-[10px] font-extrabold';
    default:
      return 'bg-[#FEF0EA] text-[#6B7280] text-[10px] font-extrabold';
  }
}

export function countCompletedRounds(
  session: Pick<BadmintonSessionListItem, 'games'> & {
    rounds?: { game?: { status: string } }[];
  }
): number {
  if (session.rounds?.length) {
    return session.rounds.filter((r) => r.game?.status === 'completed').length;
  }
  return (session.games ?? []).filter((g) => g.status === 'completed').length;
}

export function playersLabel(players: { playerName: string }[]): string {
  return players.map((p) => p.playerName).join(' & ') || '-';
}

export function playersFirstNameLabel(players: BadmintonGamePlayerItem[]): string {
  return players.map((p) => (p.playerName || '').trim().split(/\s+/)[0] || '-').join(' & ');
}

export const BADMINTON_AVATAR_COLORS = [
  'bg-[#FF5A1F] text-white',
  'bg-[#1E66F5] text-white',
  'bg-[#16A34A] text-white',
  'bg-[#9333EA] text-white',
  'bg-[#CA8A04] text-white',
  'bg-[#DB2777] text-white',
  'bg-[#0891B2] text-white',
  'bg-[#DC2626] text-white',
];

export function badmintonAvatarColor(key: string): string {
  const hash = (key || '?').split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return BADMINTON_AVATAR_COLORS[hash % BADMINTON_AVATAR_COLORS.length];
}

export function badmintonInitials(name: string): string {
  const clean = (name || '').trim();
  if (!clean) return '?';
  return clean.charAt(0).toUpperCase();
}

export function formatCourtLabel(courtNumber?: number, numCourts?: number): string | null {
  if (numCourts == null || numCourts <= 1 || courtNumber == null) return null;
  return `Lapangan ${courtNumber}`;
}

export function formatSessionScheduleLabel(session: {
  rotationFormat?: string;
  numRounds?: number;
}): string | null {
  if (!session.numRounds || session.numRounds < 1) return null;
  if (session.rotationFormat === 'random_rotation') {
    return `${session.numRounds} main per pemain`;
  }
  return `${session.numRounds} ronde`;
}

export function badmintonShortName(name: string): string {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '-';
  if (parts.length === 1) return parts[0];
  const last = parts[parts.length - 1];
  return `${parts[0]} ${last.charAt(0).toUpperCase()}.`;
}
